import {
    APS_DEPOSIT_HELD_BY_ROLE_OPTIONS,
    APS_HST_TREATMENT_OPTIONS,
    APS_IRREVOCABLE_BY_OPTIONS,
    APS_PROPERTY_TYPE_OPTIONS,
    type ApsOcrEnumOption,
} from '../types/apsOcr';

export type MatterFieldType =
    | 'text'
    | 'textarea'
    | 'address'
    | 'date'
    | 'currency'
    | 'yesNo'
    | 'count'
    | 'select'
    | 'list';

export const MATTER_FIELD_GROUPS = [
    'Matter',
    'Client',
    'Property',
    'Deposit & financial',
    'Dates',
    'RE Broker',
    'Legal description',
    'Condo',
    'Other parties',
    'Inclusions & tax',
    'Conditions & schedules',
    'Firm & Solicitor',
    'Document',
] as const;

export type MatterFieldGroup = (typeof MATTER_FIELD_GROUPS)[number];

export interface MatterFieldIntakeTag {
    /** Intake question tag. Defaults to the field key. */
    tag?: string;
    /** Tag picker label. Defaults to the field label. */
    label?: string;
    example: string;
    /** Label for a new intake question with this tag. Defaults to the tag label. */
    question?: string;
    /** Not suggested or prefilled on the other of Purchase/Sale. */
    matterType?: 'Purchase' | 'Sale';
}

export interface MatterFieldAps {
    /** Key in the scan result and the Mistral schema. Defaults to the field key. */
    key?: string;
    /** What the model should extract, sent as the field's description in the Mistral schema. */
    description: string;
}

export interface MatterField {
    /** camelCase. Legal doc placeholders, intake tags and APS keys match it regardless of case. */
    key: string;
    label: string;
    type: MatterFieldType;
    group: MatterFieldGroup;
    /** Names that genuinely differ from the key. Intake tags are already names of the field. */
    aliases?: string[];
    options?: ApsOcrEnumOption[];
    /** Spans the full row in the APS extracted fields panel. */
    wide?: boolean;
    intake?: MatterFieldIntakeTag | MatterFieldIntakeTag[];
    /** Extracted by the APS scan. */
    aps?: MatterFieldAps;
}

/**
 * Every matter fact that can be entered in the New Prospect modal, extracted from an APS,
 * filled into a legal document, or prefilled into an intake form.
 */
export const MATTER_FIELDS: MatterField[] = [
    {
        key: 'retainerAmount',
        label: 'Retainer Amount',
        type: 'currency',
        group: 'Matter',
        aliases: ['retainedAmount'],
    },
    {
        key: 'matterDescription',
        label: 'Matter Description',
        type: 'textarea',
        group: 'Matter',
        intake: { example: 'Purchase of 123 Main Street', question: 'Description of the matter' },
    },

    {
        key: 'clientAddress',
        label: 'Client Address',
        type: 'address',
        group: 'Client',
        intake: { example: '45 King Street, Toronto, ON', question: 'Client address' },
    },
    { key: 'address', label: 'Address', type: 'address', group: 'Client' },
    { key: 'dateOfBirth', label: 'Date of Birth', type: 'date', group: 'Client' },

    {
        key: 'subjectAddress',
        label: 'Subject Property Address',
        type: 'address',
        group: 'Property',
        aliases: ['propertyAddress'],
        wide: true,
        intake: { tag: 'subjectPropertyAddress', example: '123 Main Street, Toronto, ON' },
        aps: {
            key: 'propertyAddress',
            description:
                'Full civic address of the property including the city (e.g. "12 Herman Ave, Toronto"), with the postal code when printed, or empty string if unknown. Also fill municipality, province and postalCode on their own.',
        },
    },
    {
        key: 'municipality',
        label: 'Municipality',
        type: 'text',
        group: 'Property',
        intake: {
            example: 'Toronto',
            question: 'Municipality listed on the purchase and sale agreement',
        },
        aps: { description: 'Municipality/city, or empty.' },
    },
    {
        key: 'province',
        label: 'Province',
        type: 'text',
        group: 'Property',
        intake: { example: 'Ontario' },
        aps: { description: 'Province, or empty.' },
    },
    {
        key: 'postalCode',
        label: 'Postal Code',
        type: 'text',
        group: 'Property',
        intake: { example: 'M5V 2T6', question: 'Postal code of the subject property' },
        aps: { description: 'Postal code, or empty.' },
    },
    {
        key: 'propertyType',
        label: 'Property Type',
        type: 'select',
        group: 'Property',
        options: APS_PROPERTY_TYPE_OPTIONS,
        intake: { example: 'Detached' },
        aps: {
            description:
                'Dwelling type if the document describes it: Detached, Semi-detached, Condominium, Townhouse (condominium), Townhouse (freehold), Duplex, Cottage, Mobile Home, or Other. Empty if unclear. Do not infer from Form 101 alone.',
        },
    },
    {
        key: 'approximateFrontage',
        label: 'Approximate Frontage',
        type: 'text',
        group: 'Property',
        intake: { example: '25 feet' },
        aps: { description: 'Approximate frontage if stated, or empty.' },
    },
    {
        key: 'approximateDepth',
        label: 'Approximate Depth',
        type: 'text',
        group: 'Property',
        intake: { example: '120 feet' },
        aps: { description: 'Approximate depth if stated, or empty.' },
    },
    {
        key: 'approximateArea',
        label: 'Approximate Area',
        type: 'text',
        group: 'Property',
        intake: { example: '3,000 sq ft' },
        aps: { description: 'Approximate area if stated, or empty.' },
    },

    {
        key: 'purchasePrice',
        label: 'Purchase Price',
        type: 'currency',
        group: 'Deposit & financial',
        intake: [
            { example: '850000.00', matterType: 'Purchase' },
            { tag: 'salePrice', label: 'Sale Price', example: '850000.00', matterType: 'Sale' },
        ],
        aps: {
            description:
                'Numeric purchase price without currency symbols, or empty string if unknown.',
        },
    },
    {
        key: 'depositAmount',
        label: 'Deposit Amount',
        type: 'currency',
        group: 'Deposit & financial',
        intake: {
            tag: 'depositGivenPrice',
            example: '25000.00',
            question: 'Enter the deposit amount listed on your purchase and sale agreement',
        },
        aps: {
            description:
                'Numeric deposit amount without currency symbols, or empty string if unknown.',
        },
    },
    {
        key: 'depositHolder',
        label: 'Deposit Holder',
        type: 'text',
        group: 'Deposit & financial',
        intake: {
            example: 'ABC Realty Inc.',
            question: 'Deposit holder listed on the purchase and sale agreement',
        },
        aps: { description: 'Name of the deposit holder (often listing brokerage), or empty.' },
    },
    {
        key: 'depositHeldByRole',
        label: 'Deposit Held By',
        type: 'select',
        group: 'Deposit & financial',
        options: APS_DEPOSIT_HELD_BY_ROLE_OPTIONS,
        aps: {
            description:
                "Who holds the deposit: listingBrokerage (the seller's/listing brokerage, Form 100 default), coOpBrokerage, sellerLawyer, buyerLawyer, builder, or developer. Empty if unclear. The holder's name still goes in depositHolder.",
        },
    },
    {
        key: 'depositDue',
        label: 'Deposit Due',
        type: 'text',
        group: 'Deposit & financial',
        intake: { example: 'Upon Acceptance', question: 'When is the deposit due?' },
        aps: { description: 'When the deposit is due (e.g. "Upon Acceptance"), or empty.' },
    },
    {
        key: 'balancePayableHow',
        label: 'Balance Payable How',
        type: 'text',
        group: 'Deposit & financial',
        intake: { example: 'Cash or certified cheque on closing' },
        aps: { description: 'How the balance is payable (often "see Schedule A"), or empty.' },
    },

    {
        key: 'closingDate',
        label: 'Closing Date',
        type: 'date',
        group: 'Dates',
        aliases: ['completionDate'],
        intake: { example: '2026-09-15' },
        aps: {
            description:
                'The Completion Date on the APS (the closing date) as YYYY-MM-DD, or empty string if unknown.',
        },
    },
    {
        key: 'agreementDate',
        label: 'Agreement Date',
        type: 'date',
        group: 'Dates',
        intake: {
            example: '2026-08-01',
            question: 'Agreement date listed on the purchase and sale agreement',
        },
        aps: { description: 'Agreement/offer date as YYYY-MM-DD, or empty.' },
    },
    {
        key: 'confirmationOfAcceptanceDate',
        label: 'Confirmation of Acceptance Date',
        type: 'date',
        group: 'Dates',
        intake: { example: '2026-08-02', question: 'Confirmation of acceptance date' },
        aps: { description: 'Confirmation of Acceptance date as YYYY-MM-DD, or empty.' },
    },
    {
        key: 'irrevocableUntilDate',
        label: 'Irrevocable Until Date',
        type: 'date',
        group: 'Dates',
        intake: { example: '2026-08-05', question: 'Irrevocable until date' },
        aps: { description: 'Irrevocability date as YYYY-MM-DD, or empty.' },
    },
    {
        key: 'irrevocableUntilTime',
        label: 'Irrevocable Until Time',
        type: 'text',
        group: 'Dates',
        intake: { example: '11:59 p.m.', question: 'Irrevocable until time' },
        aps: { description: 'Irrevocability time as printed (e.g. "11:59 p.m."), or empty.' },
    },
    {
        key: 'irrevocableBy',
        label: 'Irrevocable By',
        type: 'select',
        group: 'Dates',
        options: APS_IRREVOCABLE_BY_OPTIONS,
        intake: { example: 'Buyer', question: 'Irrevocable by' },
        aps: {
            description:
                'Who the offer is irrevocable by, as checked or written in "This Offer shall be irrevocable by ... until": Buyer, Seller, or empty.',
        },
    },
    {
        key: 'requisitionDate',
        label: 'Requisition Date',
        type: 'date',
        group: 'Dates',
        intake: { example: '2026-08-20', question: 'Requisition date' },
        aps: { description: 'Title search / requisition date as YYYY-MM-DD, or empty.' },
    },

    {
        key: 'listingBrokerage',
        label: 'Listing Brokerage',
        type: 'text',
        group: 'RE Broker',
        intake: { example: 'ABC Realty Inc.' },
        aps: { description: 'Listing brokerage name, or empty.' },
    },
    {
        key: 'coOpBrokerage',
        label: 'Co-operating Brokerage',
        type: 'text',
        group: 'RE Broker',
        intake: { example: 'XYZ Realty Ltd.' },
        aps: { description: 'Co-operating/buyer brokerage name, or empty.' },
    },
    {
        key: 'buyerAgentName',
        label: 'Buyer Agent Name',
        type: 'text',
        group: 'RE Broker',
        intake: { example: 'Jane Buyer-Agent' },
        aps: { description: 'Buyer agent name, or empty.' },
    },
    {
        key: 'sellerAgentName',
        label: 'Seller Agent Name',
        type: 'text',
        group: 'RE Broker',
        intake: { example: 'John Listing-Agent' },
        aps: { description: 'Seller/listing agent name, or empty.' },
    },
    {
        key: 'hasRealEstateAgent',
        label: 'Has Real Estate Agent',
        type: 'yesNo',
        group: 'RE Broker',
        intake: { example: 'Yes' },
        aps: {
            description:
                'true if a brokerage or agent is clearly handling the transaction, false if clearly no agent, or empty if unclear. Do not assume true from blank brokerage lines.',
        },
    },

    {
        key: 'legalDescription',
        label: 'Legal Description',
        type: 'textarea',
        group: 'Legal description',
        wide: true,
        intake: {
            example: 'Lot 12, Plan 64M-1234',
            question: 'Legal description of the subject property',
        },
        aps: {
            description:
                'Full legal description of the property, or empty. When present, also break it into the legalDescription component fields, copying each component exactly as written.',
        },
    },
    {
        key: 'legalDescriptionPartLot',
        label: 'Legal Description Part (Lot)',
        type: 'text',
        group: 'Legal description',
        aps: {
            description:
                'Part/lot component of the legal description as printed (e.g. "Lot 12" or "Part of Lot 5"), or empty.',
        },
    },
    {
        key: 'legalDescriptionPlan',
        label: 'Legal Description Plan',
        type: 'text',
        group: 'Legal description',
        aps: { description: 'Plan component of the legal description as printed, or empty.' },
    },
    {
        key: 'legalDescriptionBeingPart',
        label: 'Legal Description Being Part',
        type: 'text',
        group: 'Legal description',
        aps: {
            description: 'Being-part component of the legal description as printed, or empty.',
        },
    },
    {
        key: 'legalDescriptionOnPlan',
        label: 'Legal Description On Plan',
        type: 'text',
        group: 'Legal description',
        aps: { description: 'On-plan component of the legal description as printed, or empty.' },
    },
    {
        key: 'legalDescriptionParcel',
        label: 'Legal Description Parcel',
        type: 'text',
        group: 'Legal description',
        aps: { description: 'Parcel component of the legal description as printed, or empty.' },
    },
    {
        key: 'legalDescriptionSection',
        label: 'Legal Description Section',
        type: 'text',
        group: 'Legal description',
        aps: { description: 'Section component of the legal description as printed, or empty.' },
    },
    {
        key: 'legalDescriptionEasement',
        label: 'Legal Description Easement/R of W',
        type: 'text',
        group: 'Legal description',
        aps: {
            description:
                'Easement/right of way component of the legal description as printed, or empty.',
        },
    },

    {
        key: 'condoCorporationNumber',
        label: 'Condo Corporation Number',
        type: 'text',
        group: 'Condo',
        intake: { example: 'MTCC 1234', question: 'Condominium corporation number' },
        aps: { description: 'Condo corporation number if Form 101, or empty.' },
    },
    {
        key: 'unitNumber',
        label: 'Unit Number',
        type: 'text',
        group: 'Condo',
        intake: { example: '1204', question: 'Unit number' },
        aps: { description: 'Condo unit number, or empty.' },
    },
    {
        key: 'levelNumber',
        label: 'Level Number',
        type: 'text',
        group: 'Condo',
        intake: { example: '12', question: 'Level number' },
        aps: { description: 'Condo level number, or empty.' },
    },
    {
        key: 'condoPlanNumber',
        label: 'Condo Plan Number',
        type: 'text',
        group: 'Condo',
        aps: {
            description:
                'Condominium plan number from the legal description (e.g. Form 101 "Condominium Plan No."), or empty.',
        },
    },
    {
        key: 'parkingExclusiveUse',
        label: 'Parking Exclusive Use',
        type: 'list',
        group: 'Condo',
        intake: { example: 'P1-42', question: 'Parking exclusive use' },
        aps: { description: 'Parking exclusive-use identifiers, each as a separate array item.' },
    },
    {
        key: 'lockerExclusiveUse',
        label: 'Locker Exclusive Use',
        type: 'list',
        group: 'Condo',
        intake: { example: 'L2-18', question: 'Locker exclusive use' },
        aps: { description: 'Locker exclusive-use identifiers, each as a separate array item.' },
    },
    {
        key: 'commonExpensesApprox',
        label: 'Common Expenses (Approx.)',
        type: 'currency',
        group: 'Condo',
        intake: { example: '650.00', question: 'Amount of monthly common expenses' },
        aps: {
            description: 'Approximate common expenses amount without currency symbols, or empty.',
        },
    },

    {
        key: 'purchaserNames',
        label: 'Purchasers',
        type: 'list',
        group: 'Other parties',
        wide: true,
        aps: {
            description:
                'Full legal names of every BUYER/PURCHASER, each as a separate array item. Take names from the opening BUYER line, buyer signatures, and any printed buyer names. Never omit this when buyer names appear. Empty array if unknown.',
        },
    },
    {
        key: 'sellerNames',
        label: 'Sellers',
        type: 'list',
        group: 'Other parties',
        wide: true,
        aps: {
            description:
                'Full legal names of every SELLER/VENDOR, each as a separate array item. Take names from the opening SELLER line, seller signatures, and Confirmation of Acceptance. Always extract sellers even when the scan is for a purchase, and buyers even when the scan is for a sale. Empty array if unknown.',
        },
    },
    {
        key: 'purchasersCount',
        label: 'Number of Purchasers',
        type: 'count',
        group: 'Other parties',
        intake: {
            tag: 'purchasersListedOnPurchaseAndSaleAgreement',
            example: '2',
            matterType: 'Purchase',
        },
        aps: {
            description:
                'Number of purchasers as digits only: as stated on the APS, otherwise the number of purchaserNames. Empty string if unknown.',
        },
    },
    {
        key: 'sellersCount',
        label: 'Number of Sellers',
        type: 'count',
        group: 'Other parties',
        intake: {
            tag: 'sellersListedOnPurchaseAndSaleAgreement',
            example: '2',
            matterType: 'Sale',
        },
        aps: {
            description:
                'Number of sellers as digits only: as stated on the APS, otherwise the number of sellerNames. Empty string if unknown.',
        },
    },
    {
        key: 'buyerNoticeEmail',
        label: 'Buyer Notice Email',
        type: 'text',
        group: 'Other parties',
        intake: { example: 'buyer.notice@xyzrealty.com' },
        aps: {
            description:
                'Email address "For delivery of Documents to Buyer" in the notices section, often the buyer\'s agent, or empty.',
        },
    },
    {
        key: 'buyerNoticeFax',
        label: 'Buyer Notice Fax',
        type: 'text',
        group: 'Other parties',
        intake: { example: '416-555-0199' },
        aps: {
            description:
                'FAX No. "For delivery of Documents to Buyer" in the notices section, or empty.',
        },
    },
    {
        key: 'sellerNoticeEmail',
        label: 'Seller Notice Email',
        type: 'text',
        group: 'Other parties',
        intake: { example: 'seller.notice@abcrealty.com' },
        aps: {
            description:
                'Email address "For delivery of Documents to Seller" in the notices section, often the seller\'s agent, or empty.',
        },
    },
    {
        key: 'sellerNoticeFax',
        label: 'Seller Notice Fax',
        type: 'text',
        group: 'Other parties',
        intake: { example: '416-555-0299' },
        aps: {
            description:
                'FAX No. "For delivery of Documents to Seller" in the notices section, or empty.',
        },
    },
    {
        key: 'hasSpousalConsent',
        label: 'Has Spousal Consent',
        type: 'yesNo',
        group: 'Other parties',
        intake: { example: 'No' },
        aps: { description: 'true, false, or empty.' },
    },

    {
        key: 'chattelsIncluded',
        label: 'Chattels Included',
        type: 'textarea',
        group: 'Inclusions & tax',
        wide: true,
        intake: {
            example: 'Fridge, stove, dishwasher',
            question: 'Chattels included in the purchase and sale agreement',
        },
        aps: { description: 'Chattels included text, or empty.' },
    },
    {
        key: 'fixturesExcluded',
        label: 'Fixtures Excluded',
        type: 'textarea',
        group: 'Inclusions & tax',
        wide: true,
        intake: {
            example: 'Dining room chandelier',
            question: 'Fixtures excluded from the purchase and sale agreement',
        },
        aps: { description: 'Fixtures excluded text, or empty.' },
    },
    {
        key: 'rentalItems',
        label: 'Rental Items',
        type: 'textarea',
        group: 'Inclusions & tax',
        wide: true,
        intake: { example: 'Hot water tank, alarm system, furnace' },
        aps: { description: 'Rental items text, or empty.' },
    },
    {
        key: 'hstTreatment',
        label: 'HST Treatment',
        type: 'select',
        group: 'Inclusions & tax',
        options: APS_HST_TREATMENT_OPTIONS,
        intake: {
            example: 'includedIn',
            question: 'HST treatment listed on the purchase and sale agreement',
        },
        aps: { description: 'includedIn, inAdditionTo, or empty if unclear.' },
    },

    {
        key: 'hasFinancingCondition',
        label: 'Has Financing Condition',
        type: 'yesNo',
        group: 'Conditions & schedules',
        intake: {
            example: 'Yes',
            question: 'Does the purchase and sale agreement have a financing condition?',
        },
        aps: { description: 'true, false, or empty.' },
    },
    {
        key: 'hasInspectionCondition',
        label: 'Has Inspection Condition',
        type: 'yesNo',
        group: 'Conditions & schedules',
        intake: {
            example: 'No',
            question: 'Does the purchase and sale agreement have an inspection condition?',
        },
        aps: { description: 'true, false, or empty.' },
    },
    {
        key: 'hasSaleOfBuyerPropertyCondition',
        label: 'Has Sale of Buyer Property Condition',
        type: 'yesNo',
        group: 'Conditions & schedules',
        intake: {
            example: 'No',
            question:
                'Does the purchase and sale agreement have a sale-of-buyer-property condition?',
        },
        aps: { description: 'true, false, or empty.' },
    },
    {
        key: 'hasStatusCertificateCondition',
        label: 'Has Status Certificate Condition',
        type: 'yesNo',
        group: 'Conditions & schedules',
        intake: {
            example: 'Yes',
            question: 'Does the purchase and sale agreement have a status certificate condition?',
        },
        aps: { description: 'true, false, or empty.' },
    },
    {
        key: 'otherConditionsSummary',
        label: 'Other Conditions Summary',
        type: 'textarea',
        group: 'Conditions & schedules',
        wide: true,
        intake: {
            example: 'Conditional on review of status certificate',
            question: 'Other conditions listed on the purchase and sale agreement',
        },
        aps: {
            description:
                'Brief summary of Schedule A conditions not covered by the condition flags, or empty. Do not paste the entire schedule unless short.',
        },
    },
    {
        key: 'scheduleAPresent',
        label: 'Schedule A Present',
        type: 'yesNo',
        group: 'Conditions & schedules',
        intake: { example: 'Yes' },
        aps: { description: 'true, false, or empty.' },
    },
    {
        key: 'scheduleBPresent',
        label: 'Schedule B Present',
        type: 'yesNo',
        group: 'Conditions & schedules',
        intake: { example: 'No' },
        aps: { description: 'true, false, or empty.' },
    },
    {
        key: 'schedulesAttached',
        label: 'Schedules Attached',
        type: 'list',
        group: 'Conditions & schedules',
        intake: { example: 'A' },
        aps: {
            description:
                'Letters or names of attached schedules (e.g. "A", "B"), each as a separate array item.',
        },
    },

    {
        key: 'buyerLawyerName',
        label: 'Buyer Lawyer Name',
        type: 'text',
        group: 'Firm & Solicitor',
        intake: {
            example: 'Sarah Buyer-Lawyer',
            question: 'Purchaser solicitor name listed on the purchase and sale agreement',
        },
        aps: { description: 'Buyer lawyer name, or empty.' },
    },
    {
        key: 'buyerLawyerFirm',
        label: 'Buyer Lawyer Firm',
        type: 'text',
        group: 'Firm & Solicitor',
        intake: {
            example: 'Buyer Law LLP',
            question: 'Purchaser solicitor firm listed on the purchase and sale agreement',
        },
        aps: { description: 'Buyer lawyer firm, or empty.' },
    },
    {
        key: 'buyerLawyerAddress',
        label: 'Buyer Lawyer Address',
        type: 'address',
        group: 'Firm & Solicitor',
        intake: {
            example: '100 King Street West, Toronto, ON',
            question: 'Purchaser solicitor address listed on the purchase and sale agreement',
        },
        aps: { description: 'Buyer lawyer address, or empty.' },
    },
    {
        key: 'buyerLawyerPhone',
        label: 'Buyer Lawyer Phone',
        type: 'text',
        group: 'Firm & Solicitor',
        intake: {
            example: '416-555-0101',
            question: 'Purchaser solicitor phone listed on the purchase and sale agreement',
        },
        aps: { description: 'Buyer lawyer phone, or empty.' },
    },
    {
        key: 'buyerLawyerEmail',
        label: 'Buyer Lawyer Email',
        type: 'text',
        group: 'Firm & Solicitor',
        intake: {
            example: 'sbuyer@buyerlaw.com',
            question: 'Purchaser solicitor email listed on the purchase and sale agreement',
        },
        aps: { description: 'Buyer lawyer email, or empty.' },
    },
    {
        key: 'sellerLawyerName',
        label: 'Seller Lawyer Name',
        type: 'text',
        group: 'Firm & Solicitor',
        intake: {
            example: 'Mark Seller-Lawyer',
            question: 'Vendor solicitor name listed on the purchase and sale agreement',
        },
        aps: { description: 'Seller lawyer name, or empty.' },
    },
    {
        key: 'sellerLawyerFirm',
        label: 'Seller Lawyer Firm',
        type: 'text',
        group: 'Firm & Solicitor',
        intake: {
            example: 'Seller Law LLP',
            question: 'Vendor solicitor firm listed on the purchase and sale agreement',
        },
        aps: { description: 'Seller lawyer firm, or empty.' },
    },
    {
        key: 'sellerLawyerAddress',
        label: 'Seller Lawyer Address',
        type: 'address',
        group: 'Firm & Solicitor',
        intake: {
            example: '200 Bay Street, Toronto, ON',
            question: 'Vendor solicitor address listed on the purchase and sale agreement',
        },
        aps: { description: 'Seller lawyer address, or empty.' },
    },
    {
        key: 'sellerLawyerPhone',
        label: 'Seller Lawyer Phone',
        type: 'text',
        group: 'Firm & Solicitor',
        intake: {
            example: '416-555-0202',
            question: 'Vendor solicitor phone listed on the purchase and sale agreement',
        },
        aps: { description: 'Seller lawyer phone, or empty.' },
    },
    {
        key: 'sellerLawyerEmail',
        label: 'Seller Lawyer Email',
        type: 'text',
        group: 'Firm & Solicitor',
        intake: {
            example: 'mseller@sellerlaw.com',
            question: 'Vendor solicitor email listed on the purchase and sale agreement',
        },
        aps: { description: 'Seller lawyer email, or empty.' },
    },

    {
        key: 'formNumber',
        label: 'Form Number',
        type: 'text',
        group: 'Document',
        intake: { example: '100' },
        aps: { description: 'OREA form number such as "100" or "101", or empty.' },
    },
    {
        key: 'formRevisionDate',
        label: 'Form Revision Date',
        type: 'text',
        group: 'Document',
        intake: { example: '2023-11' },
        aps: { description: 'Form revision/date printed on the form, or empty.' },
    },
    {
        key: 'mlsNumber',
        label: 'MLS Number',
        type: 'text',
        group: 'Document',
        intake: { example: 'C1234567', question: 'MLS number' },
        aps: { description: 'MLS number if present, or empty.' },
    },
];
